<script setup>
import { computed } from 'vue';
import { useBreakpoints } from '@vueuse/core';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { formatNumber } from '@chatwoot/utils';
import wootConstants from 'dashboard/constants/globals';
import { Badge } from 'next/polaris/ui/badge';

import ConversationBasicFilter from './widgets/conversation/ConversationBasicFilter.vue';
import SwitchLayout from 'dashboard/routes/dashboard/conversation/search/SwitchLayout.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  pageTitle: { type: String, required: true },
  hasAppliedFilters: { type: Boolean, required: true },
  hasActiveFolders: { type: Boolean, required: true },
  canManageFolders: { type: Boolean, default: true },
  isOnExpandedLayout: { type: Boolean, required: true },
  // The list's own count, computed by ChatList.vue (headerCount): the tier-2 menu's Mine/Unassigned/All
  // count on an unfiltered list; the filter endpoint's result count on an applied filter or a folder.
  // `null` (or 0) while that count isn't known yet — the badge is hidden.
  count: { type: Number, default: null },
});

const emit = defineEmits([
  'addFolders',
  'deleteFolders',
  'resetFilters',
  'basicFilterChange',
  'filtersModal',
]);

const { uiSettings, updateUISettings } = useUISettings();

const onBasicFilterChange = (value, type) => {
  emit('basicFilterChange', value, type);
};

const hasAppliedFiltersOrActiveFolders = computed(() => {
  return props.hasAppliedFilters || props.hasActiveFolders;
});

const showBasicFilter = computed(() => !hasAppliedFiltersOrActiveFolders.value);

// The selects sit on the title row only where the rows are the expanded table (ConversationList.vue's
// showExpandedCards: the expanded preference at >= lg); anywhere narrower they stack below the title.
const breakpoints = useBreakpoints({
  lg: wootConstants.LARGE_SCREEN_BREAKPOINT,
});
const isLgScreen = breakpoints.greaterOrEqual('lg');
const showsSelectsInline = computed(
  () => props.isOnExpandedLayout && isLgScreen.value
);

const hasCount = computed(() => Boolean(props.count));
const formattedCount = computed(() => formatNumber(props.count));

const toggleConversationLayout = () => {
  const { LAYOUT_TYPES } = wootConstants;
  const {
    conversation_display_type: conversationDisplayType = LAYOUT_TYPES.CONDENSED,
  } = uiSettings.value;
  const newViewType =
    conversationDisplayType === LAYOUT_TYPES.CONDENSED
      ? LAYOUT_TYPES.EXPANDED
      : LAYOUT_TYPES.CONDENSED;
  updateUISettings({
    conversation_display_type: newViewType,
    previously_used_conversation_display_type: newViewType,
  });
};
</script>

<template>
  <div
    class="flex flex-col shrink-0"
    :class="{
      'border-b border-solid border-polaris-border':
        hasAppliedFiltersOrActiveFolders,
    }"
  >
    <div class="flex h-[3.25rem] items-center justify-between gap-2 px-4">
      <div class="flex min-w-0 items-center gap-2">
        <h1
          class="min-w-0 truncate type-polaris-section-title text-polaris-foreground"
          :title="pageTitle"
        >
          {{ pageTitle }}
        </h1>
        <Badge v-if="hasCount" variant="secondary" class="shrink-0">
          {{ formattedCount }}
        </Badge>
      </div>

      <div v-if="showsSelectsInline" class="flex items-center gap-2 shrink-0">
        <div v-if="showBasicFilter" class="grid grid-cols-2 gap-2 w-72">
          <ConversationBasicFilter @change-filter="onBasicFilterChange" />
        </div>

        <div class="flex items-center gap-1">
          <template v-if="hasAppliedFilters && !hasActiveFolders">
            <div class="relative">
              <NextButton
                v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.ADD.SAVE_BUTTON')"
                :aria-label="$t('FILTER.CUSTOM_VIEWS.ADD.SAVE_BUTTON')"
                icon="i-lucide-save"
                ghost
                xs
                @click="emit('addFolders')"
              />
              <div
                id="saveFilterTeleportTarget"
                class="absolute z-50 mt-2"
                :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
              />
            </div>
            <NextButton
              v-tooltip.top-end="$t('FILTER.CLEAR_BUTTON_LABEL')"
              :aria-label="$t('FILTER.CLEAR_BUTTON_LABEL')"
              icon="i-lucide-circle-x"
              ruby
              ghost
              xs
              @click="emit('resetFilters')"
            />
          </template>
          <template v-if="hasActiveFolders">
            <div v-if="canManageFolders" class="relative">
              <NextButton
                id="toggleConversationFilterButton"
                v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.EDIT.EDIT_BUTTON')"
                :aria-label="$t('FILTER.CUSTOM_VIEWS.EDIT.EDIT_BUTTON')"
                icon="i-lucide-pen-line"
                ghost
                xs
                @click="emit('filtersModal')"
              />
              <div
                id="conversationFilterTeleportTarget"
                class="absolute z-50 mt-2"
                :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
              />
            </div>
            <NextButton
              v-if="canManageFolders"
              id="toggleConversationFilterButton"
              v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.DELETE.DELETE_BUTTON')"
              :aria-label="$t('FILTER.CUSTOM_VIEWS.DELETE.DELETE_BUTTON')"
              icon="i-lucide-trash-2"
              ruby
              ghost
              xs
              @click="emit('deleteFolders')"
            />
          </template>
          <div v-else class="relative">
            <NextButton
              id="toggleConversationFilterButton"
              v-tooltip.right="$t('FILTER.TOOLTIP_LABEL')"
              :aria-label="$t('FILTER.TOOLTIP_LABEL')"
              icon="i-lucide-list-filter"
              ghost
              xs
              @click="emit('filtersModal')"
            />
            <div
              id="conversationFilterTeleportTarget"
              class="absolute z-50 mt-2"
              :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
            />
          </div>
          <SwitchLayout
            :is-on-expanded-layout="isOnExpandedLayout"
            @toggle="toggleConversationLayout"
          />
        </div>
      </div>
    </div>

    <div v-if="!showsSelectsInline" class="flex flex-col gap-2 px-4 pb-3">
      <div class="flex items-center justify-end gap-1">
        <template v-if="hasAppliedFilters && !hasActiveFolders">
          <div class="relative">
            <NextButton
              v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.ADD.SAVE_BUTTON')"
              :aria-label="$t('FILTER.CUSTOM_VIEWS.ADD.SAVE_BUTTON')"
              icon="i-lucide-save"
              ghost
              xs
              @click="emit('addFolders')"
            />
            <div
              id="saveFilterTeleportTarget"
              class="absolute z-50 mt-2"
              :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
            />
          </div>
          <NextButton
            v-tooltip.top-end="$t('FILTER.CLEAR_BUTTON_LABEL')"
            :aria-label="$t('FILTER.CLEAR_BUTTON_LABEL')"
            icon="i-lucide-circle-x"
            ruby
            ghost
            xs
            @click="emit('resetFilters')"
          />
        </template>
        <template v-if="hasActiveFolders">
          <div v-if="canManageFolders" class="relative">
            <NextButton
              id="toggleConversationFilterButton"
              v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.EDIT.EDIT_BUTTON')"
              :aria-label="$t('FILTER.CUSTOM_VIEWS.EDIT.EDIT_BUTTON')"
              icon="i-lucide-pen-line"
              ghost
              xs
              @click="emit('filtersModal')"
            />
            <div
              id="conversationFilterTeleportTarget"
              class="absolute z-50 mt-2"
              :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
            />
          </div>
          <NextButton
            v-if="canManageFolders"
            id="toggleConversationFilterButton"
            v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.DELETE.DELETE_BUTTON')"
            :aria-label="$t('FILTER.CUSTOM_VIEWS.DELETE.DELETE_BUTTON')"
            icon="i-lucide-trash-2"
            ruby
            ghost
            xs
            @click="emit('deleteFolders')"
          />
        </template>
        <div v-else class="relative">
          <NextButton
            id="toggleConversationFilterButton"
            v-tooltip.right="$t('FILTER.TOOLTIP_LABEL')"
            :aria-label="$t('FILTER.TOOLTIP_LABEL')"
            icon="i-lucide-list-filter"
            ghost
            xs
            @click="emit('filtersModal')"
          />
          <div
            id="conversationFilterTeleportTarget"
            class="absolute z-50 mt-2"
            :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
          />
        </div>
        <SwitchLayout
          :is-on-expanded-layout="isOnExpandedLayout"
          @toggle="toggleConversationLayout"
        />
      </div>

      <div v-if="showBasicFilter" class="grid gap-2">
        <ConversationBasicFilter @change-filter="onBasicFilterChange" />
      </div>
    </div>
  </div>
</template>
